import { useEffect, useMemo, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { Box, Alert } from "@mui/material";
import { useSnackbar } from "notistack";
import DropboxFolderDrawer from "./DropboxFolderDrawerRefactored";
import FolderFilters from "./folders/FolderFilters";
import FolderList from "./folders/FolderList";
import SettingsDialog from "./folders/SettingsDialog";
import DeepSearch from "./folders/DeepSearch";
import { useFilteredFolders } from "../hooks/useFilteredFolders";
import {
  getDropboxFolders,
  resetState,
  clearError,
  updateDropboxFolder,
} from "../../../../redux/slices/dropboxFoldersSlice";
import { fetchCustomers } from "../../../../redux/slices/customersSlice";
import { runDropboxSync } from "../../../../services/api/dropboxSyncService";
import { PageHeader } from "../../../../ui";

const DropboxFolders = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    folders,
    loading,
    error,
    lastSyncTime: rawSyncTime,
  } = useAppSelector((state) => state.dropboxFolders);
  const { customers } = useAppSelector((state) => state.customers);

  const userRole = useAppSelector((state) => state.login.user?.role);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPath, setSelectedPath] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<any>(null);
  const [navigateToPath, setNavigateToPath] = useState<any>(null);
  const [highlightPath, setHighlightPath] = useState<any>(null);

  // Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [selectedProjects, setSelectedProjects] = useState<any[]>([]);
  const [displayName, setDisplayName] = useState("");

  const { filteredFolders, uniquePaths } = useFilteredFolders(
    folders,
    searchTerm,
    selectedPath,
    sortBy,
    sortOrder,
  );

  useEffect(() => {
    // Load folders on component mount
    dispatch(getDropboxFolders());
    // Load customers for the settings dialog
    dispatch(fetchCustomers());

    // Cleanup on unmount
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Error loading folders: ${error}`, { variant: "error" });
      dispatch(clearError());
    }
  }, [error, enqueueSnackbar, dispatch]);

  const handleRefresh = () => {
    dispatch(getDropboxFolders());
  };

  const [syncing, setSyncing] = useState(false);

  // Manually trigger the dropbox-connector sync (admin only). The sync runs
  // asynchronously on the connector; folders update within ~10 minutes.
  const handleRunSync = async () => {
    setSyncing(true);
    try {
      const res = await runDropboxSync();
      if (res?.status === "already_in_progress") {
        enqueueSnackbar("A Dropbox sync is already running.", {
          variant: "info",
        });
      } else {
        enqueueSnackbar(
          "Dropbox sync started. Folders update within ~10 minutes.",
          { variant: "success" }
        );
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed to start Dropbox sync.", {
        variant: "error",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handlePathFilter = (path: any) => {
    setSelectedPath(selectedPath === path ? "" : path);
  };

  const handleSortChange = (field: any, order: any) => {
    setSortBy(field);
    setSortOrder(order);
  };

  const handleFolderClick = (folder: any) => {
    setSelectedFolderId(folder._id);
    setNavigateToPath(null);
    setHighlightPath(null);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedFolderId(null);
    setNavigateToPath(null);
    setHighlightPath(null);
  };

  const handleSettingsClick = (event: any, folder: any) => {
    event.stopPropagation();
    setSelectedFolder(folder);
    setSelectedProjects(folder.assignedProjects || []);
    setDisplayName(folder.displayName || "");
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    setSelectedFolder(null);
    setSelectedProjects([]);
    setDisplayName("");
  };

  const handleSaveSettings = () => {
    if (!selectedFolder) return;

    const projectsAssigned = selectedProjects.map((p) => p.id || p._id);

    dispatch(
      updateDropboxFolder({
        folderId: selectedFolder._id,
        projectsAssigned,
        displayName: displayName || "",
      }),
    )
      .unwrap()
      .then(() => {
        enqueueSnackbar("Folder settings updated successfully", {
          variant: "success",
        });
        handleSettingsClose();
        handleRefresh();
      })
      .catch((err) => {
        enqueueSnackbar(`Failed to update settings: ${err.message || err}`, {
          variant: "error",
        });
      });
  };

  const handleProjectsChange = (_event: any, newValue: any) => {
    setSelectedProjects(newValue);
  };

  const handleDisplayNameChange = (newDisplayName: any) => {
    setDisplayName(newDisplayName);
  };

  const handleSearchResultClick = (item: any) => {
    // For nested results (files inside raw_data.children), open the parent folder
    // and auto-navigate to the file's location in the tree
    const folderId = item.isNested ? item.parentId : item._id;
    if (folderId) {
      setSelectedFolderId(folderId);
      // Pass the file's path so the drawer can auto-navigate to its parent folder
      setNavigateToPath(item.isNested ? item.path || item.path_display : null);
      setHighlightPath(item.path || item.path_display || null);
      setDrawerOpen(true);
    }
  };

  const lastSyncTime = useMemo(() => {
    return rawSyncTime
      ? new Date(rawSyncTime).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;
  }, [rawSyncTime]);

  // Show initial loading state or no folders message
  if (!loading && folders.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <PageHeader
          eyebrow="Documents"
          title="Dropbox Folders"
          summary="The job folders the office shares with clients, and the project each one belongs to."
        />
        <FolderFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          selectedPath={selectedPath}
          onPathFilter={handlePathFilter}
          uniquePaths={uniquePaths}
          onRefresh={handleRefresh}
          loading={loading}
          filteredFolders={filteredFolders}
          totalFolders={folders.length}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          onRunSync={handleRunSync}
          syncing={syncing}
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          No Dropbox folders found. Make sure your Dropbox account is connected.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PageHeader
        eyebrow="Documents"
        title="Dropbox Folders"
        summary="The job folders the office shares with clients, and the project each one belongs to."
      />
      <FolderFilters
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        selectedPath={selectedPath}
        onPathFilter={handlePathFilter}
        uniquePaths={uniquePaths}
        onRefresh={handleRefresh}
        loading={loading}
        filteredFolders={filteredFolders}
        totalFolders={folders.length}
        lastSyncTime={lastSyncTime}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        onRunSync={handleRunSync}
        syncing={syncing}
      />

      <DeepSearch onResultClick={handleSearchResultClick} />

      <FolderList
        folders={filteredFolders}
        loading={loading}
        error={error}
        userRole={userRole}
        onRefresh={handleRefresh}
        onFolderClick={handleFolderClick}
        onSettingsClick={handleSettingsClick}
      />

      <DropboxFolderDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        folderId={selectedFolderId}
        userRole={userRole}
        navigateToPath={navigateToPath}
        highlightPath={highlightPath}
        onClearHighlight={() => setHighlightPath(null)}
      />

      <SettingsDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        selectedFolder={selectedFolder}
        selectedProjects={selectedProjects}
        onProjectsChange={handleProjectsChange}
        customers={customers}
        onSave={handleSaveSettings}
        displayName={displayName}
        onDisplayNameChange={handleDisplayNameChange}
      />
    </Box>
  );
};

export default DropboxFolders;
